import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import Footer from '../home/components/Footer';
import PricingFAQ from './components/PricingFAQ';

const PricingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [postcode, setPostcode] = useState('');
  const [isValidPostcode, setIsValidPostcode] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [locationData, setLocationData] = useState<any>(null);
  const [cityPricing, setCityPricing] = useState<any>(null);
  const [isAfterHours, setIsAfterHours] = useState(false);
  const [isShortNotice, setIsShortNotice] = useState(false);

  // Handle postcode submit
  const handlePostcodeSubmit = async () => {
    if (!postcode.trim()) {
      setValidationError('Please enter a postcode');
      return;
    }

    setIsValidating(true);
    setValidationError('');

    try {
      const { data, error } = await supabase.functions.invoke('validate-postcode', {
        body: { postcode: postcode.trim() },
      });

      if (error) throw error;

      if (data.valid && data.city) {
        setIsValidPostcode(true);
        setLocationData({
          city: data.city,
          region: data.region,
          latitude: data.latitude,
          longitude: data.longitude,
        });
        setValidationError('');

        const citySlug = data.city.toLowerCase().replace(/\s+/g, '-');
        const {
          data: pricingData,
          error: pricingError,
        } = await supabase.functions.invoke('get-city-pricing', {
          body: { city: citySlug },
        });

        if (!pricingError && pricingData) {
          const pricingMap: any = {};
          if (pricingData.pricing && Array.isArray(pricingData.pricing)) {
            pricingData.pricing.forEach((item: any) => {
              const serviceKey = item.service_type.toLowerCase();
              pricingMap[serviceKey] = {
                first_hour: item.hourly_rate,
                half_hour: item.half_hour_rate,
              };
            });
          }
          setCityPricing(pricingMap);
        }
      } else {
        setValidationError(data.message || "Sorry, we're not in your area yet");
        setIsValidPostcode(false);
        setLocationData(null);
        setCityPricing(null);
      }
    } catch (error) {
      console.error('Postcode validation error:', error);
      setValidationError("Sorry, we're not in your area yet");
      setIsValidPostcode(false);
      setLocationData(null);
      setCityPricing(null);
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <Link to="/" className="flex items-center">
              <img
                src="https://static.readdy.ai/image/a3382d87126fc476a032aa8f5ebcbb38/e0dadf219d3b341a9184e92001cfe9b6.png"
                alt="Taskbudy"
                className="h-7 sm:h-8 lg:h-12 w-auto"
              />
            </Link>

            <div className="hidden lg:flex items-center space-x-8">
              <Link to="/" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                Home
              </Link>
              <Link to="/services" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                Services
              </Link>
              <Link to="/locations" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                Locations
              </Link>
              <Link to="/pricing" className="text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                Pricing
              </Link>
              <Link to="/about" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                About
              </Link>
              <Link to="/contact" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                Contact
              </Link>
              <Link to="/auth/signin" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                Sign In
              </Link>
              <Link to="/auth/signup" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                Sign Up
              </Link>
              <Link
                to="/provider-signup"
                className="bg-[#fe7a14] hover:bg-[#e56a0f] text-white px-6 py-3 rounded-xl font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                Become a Provider
              </Link>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-[#0F2A44] w-10 h-10 flex items-center justify-center cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="fixed top-[64px] sm:top-[80px] left-0 right-0 bg-white shadow-lg z-40 border-t border-gray-200 animate-slideDown">
              <div className="flex flex-col py-2 max-h-[calc(100vh-64px)] sm:max-h-[calc(100vh-80px)] overflow-y-auto">
                <Link
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1"
                >
                  Home
                </Link>
                <Link
                  to="/services"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1"
                >
                  Services
                </Link>
                <Link
                  to="/locations"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1"
                >
                  Locations
                </Link>
                <Link
                  to="/pricing"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-[#fe7a14] bg-orange-50 font-medium transition-all duration-300 cursor-pointer"
                >
                  Pricing
                </Link>
                <Link
                  to="/about"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1"
                >
                  About
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1"
                >
                  Contact
                </Link>
                <div className="border-t border-gray-200 my-2"></div>
                <Link
                  to="/auth/signin"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1"
                >
                  Sign In
                </Link>
                <Link
                  to="/auth/signup"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1"
                >
                  Sign Up
                </Link>
                <Link
                  to="/provider-signup"
                  onClick={() => setMobileMenuOpen(false)}
                  className="mx-4 my-2 px-4 py-3 bg-gradient-to-r from-[#fe7a14] to-[#ff8c3a] text-white font-bold rounded-lg text-center hover:from-[#e56a0f] hover:to-[#fe7a14] transition-all duration-300 cursor-pointer whitespace-nowrap transform hover:scale-105 shadow-md"
                >
                  Become a Provider
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-20 pb-8 sm:pt-24 sm:pb-12 md:pt-32 md:pb-16 lg:pt-36 lg:pb-20 overflow-hidden">
        {/* Background Design */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-orange-50/30"></div>

        {/* Large Colorful Gradient Blobs - More responsive sizing */}
        <div className="absolute -top-16 -left-16 sm:-top-24 sm:-left-24 md:-top-32 md:-left-32 w-[180px] sm:w-[280px] md:w-[450px] lg:w-[600px] h-[180px] sm:h-[280px] md:h-[450px] lg:h-[600px] bg-gradient-to-br from-orange-400 to-amber-300 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute top-1/3 -right-16 sm:-right-32 md:-right-48 w-[200px] sm:w-[350px] md:w-[500px] lg:w-[700px] h-[200px] sm:h-[350px] md:h-[500px] lg:h-[700px] bg-gradient-to-bl from-teal-400 to-cyan-300 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute -bottom-16 left-1/4 w-[150px] sm:w-[220px] md:w-[350px] lg:w-[500px] h-[150px] sm:h-[220px] md:h-[350px] lg:h-[500px] bg-gradient-to-br from-orange-300 to-yellow-200 rounded-full opacity-25 blur-3xl"></div>

        {/* Decorative Mesh Grid - Responsive grid size */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
            linear-gradient(to right, rgba(255, 117, 31, 0.05) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255, 117, 31, 0.05) 1px, transparent 1px)
          `,
            backgroundSize: '24px 24px',
          }}
        ></div>
        <div
          className="absolute inset-0 hidden sm:block"
          style={{
            backgroundImage: `
            linear-gradient(to right, rgba(255, 117, 31, 0.06) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255, 117, 31, 0.06) 1px, transparent 1px)
          `,
            backgroundSize: '32px 32px',
          }}
        ></div>
        <div
          className="absolute inset-0 hidden md:block"
          style={{
            backgroundImage: `
            linear-gradient(to right, rgba(255, 117, 31, 0.06) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255, 117, 31, 0.06) 1px, transparent 1px)
          `,
            backgroundSize: '40px 40px',
          }}
        ></div>

        {/* Floating Geometric Shapes - More responsive with tablet breakpoint */}
        <div className="absolute top-20 left-8 w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-32 lg:h-32 border-[3px] sm:border-[4px] md:border-[5px] border-orange-300/40 sm:border-orange-300/50 rounded-2xl sm:rounded-3xl rotate-12 hidden sm:block"></div>
        <div className="absolute top-32 right-8 sm:right-16 md:right-24 w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 border-[3px] sm:border-[4px] md:border-[5px] border-teal-300/40 sm:border-teal-300/50 rounded-full hidden sm:block"></div>
        <div className="absolute bottom-16 left-16 sm:left-24 md:left-32 w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 lg:w-20 lg:h-20 bg-gradient-to-br from-orange-200 to-orange-100 rounded-full opacity-50 sm:opacity-60 hidden md:block"></div>
        <div className="absolute bottom-24 right-8 sm:right-12 md:right-16 w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 border-[3px] sm:border-[4px] md:border-[5px] border-amber-300/30 sm:border-amber-300/40 rounded-2xl sm:rounded-3xl -rotate-12 hidden sm:block"></div>

        {/* Accent Circles - Responsive positioning */}
        <div className="absolute top-28 sm:top-32 left-1/4 sm:left-1/3 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 bg-orange-400 rounded-full opacity-30 sm:opacity-40 hidden sm:block"></div>
        <div className="absolute top-48 sm:top-56 right-1/3 sm:right-1/4 w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 bg-teal-400 rounded-full opacity-40 sm:opacity-50 hidden sm:block"></div>
        <div className="absolute bottom-32 sm:bottom-40 left-[15%] sm:left-1/5 w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 bg-amber-400 rounded-full opacity-30 sm:opacity-40 hidden md:block"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-1.5 sm:gap-2 bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-200/60 text-orange-600 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 md:mb-6">
            <i className="ri-price-tag-3-fill text-sm sm:text-base"></i>
            <span>Transparent Pricing</span>
          </div>

          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-[#22304e] mb-2 sm:mb-3 md:mb-6 leading-tight">
            Clear, Upfront Pricing
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500 mt-1 md:mt-2">
              No Hidden Fees
            </span>
          </h1>

          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-4 sm:mb-6 md:mb-10 px-2">
            Know exactly what you&apos;ll pay before you book. Our transparent pricing model means no quotes, no negotiations, and no
            surprises.
          </p>

          {/* Trust Indicators */}
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 md:gap-6 lg:gap-10">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg md:rounded-xl flex items-center justify-center">
                <i className="ri-shield-check-fill text-white text-xs sm:text-sm md:text-lg"></i>
              </div>
              <span className="text-[#22304e] font-medium text-xs sm:text-sm md:text-base">No Hidden Fees</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg md:rounded-xl flex items-center justify-center">
                <i className="ri-time-fill text-white text-xs sm:text-sm md:text-lg"></i>
              </div>
              <span className="text-[#22304e] font-medium text-xs sm:text-sm md:text-base">Hourly Rates</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 bg-gradient-to-br from-rose-500 to-pink-500 rounded-lg md:rounded-xl flex items-center justify-center">
                <i className="ri-map-pin-fill text-white text-xs sm:text-sm md:text-lg"></i>
              </div>
              <span className="text-[#22304e] font-medium text-xs sm:text-sm md:text-base">Location-Based</span>
            </div>
          </div>
        </div>
      </section>

      {/* Postcode Input Section */}
      <section className="py-4 sm:py-6 md:py-10 lg:py-12 relative overflow-hidden -mt-8 sm:-mt-12 md:-mt-16 lg:-mt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-slate-50 to-orange-50/20"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            {/* Outer glow wrapper */}
            <div className="relative">
              {/* Glow effect */}
              <div className="absolute -inset-1 sm:-inset-1.5 md:-inset-2 bg-gradient-to-r from-orange-400 via-amber-400 to-orange-400 rounded-2xl sm:rounded-3xl blur-lg opacity-30 animate-pulse"></div>
              
              {/* Main card */}
              <div className="relative bg-white rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl border-2 border-orange-200/50 p-5 sm:p-6 md:p-8 lg:p-10 overflow-hidden">
                {/* Decorative corner accents */}
                <div className="absolute top-0 left-0 w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-gradient-to-br from-orange-100 to-transparent rounded-br-full opacity-60"></div>
                <div className="absolute bottom-0 right-0 w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-gradient-to-tl from-amber-100 to-transparent rounded-tl-full opacity-60"></div>
                
                {/* Top gradient bar */}
                <div className="absolute top-0 left-0 right-0 h-1 sm:h-1.5 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-500"></div>
                
                <div className="relative text-center mb-5 sm:mb-6 md:mb-8">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-18 md:h-18 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg shadow-orange-500/30 ring-4 ring-orange-100">
                    <i className="ri-search-line text-xl sm:text-2xl md:text-3xl text-white"></i>
                  </div>
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#22304e] mb-2 sm:mb-3">Get Pricing for Your Area</h2>
                  <p className="text-gray-600 text-sm sm:text-base">Enter your postcode to see exact pricing for services in your location</p>
                </div>

                <div className="relative flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
                  <input
                    type="text"
                    value={postcode}
                    onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                    placeholder="Enter postcode (e.g., SW1A 1AA)"
                    className="flex-1 px-4 sm:px-5 py-3.5 sm:py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#fe7a14] focus:ring-4 focus:ring-[#fe7a14]/20 text-sm sm:text-base transition-all bg-gray-50/50 hover:bg-white"
                  />
                  <button
                    onClick={handlePostcodeSubmit}
                    disabled={isValidating}
                    className="px-6 sm:px-8 py-3.5 sm:py-4 bg-gradient-to-r from-[#fe7a14] to-[#ff8c3a] text-white rounded-xl font-semibold hover:from-[#e56a0f] hover:to-[#fe7a14] transition-all whitespace-nowrap disabled:opacity-50 cursor-pointer shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40 hover:scale-105 text-sm sm:text-base"
                  >
                    {isValidating ? 'Checking...' : 'Check Pricing'}
                  </button>
                </div>

                {validationError && (
                  <div className="mt-4 flex items-center justify-center gap-2 text-red-600 bg-red-50 py-2.5 px-4 rounded-lg mx-auto max-w-md">
                    <i className="ri-error-warning-line text-sm sm:text-base"></i>
                    <p className="text-xs sm:text-sm font-medium">{validationError}</p>
                  </div>
                )}
                {isValidPostcode && locationData && (
                  <div className="mt-4 flex items-center justify-center gap-2 text-green-600 bg-green-50 py-2.5 px-4 rounded-lg mx-auto max-w-md">
                    <i className="ri-check-line text-base sm:text-lg"></i>
                    <p className="text-xs sm:text-sm font-semibold">
                      Service available in {locationData.city}, {locationData.region}
                    </p>
                  </div>
                )}
                
                {/* Bottom trust indicators */}
                <div className="relative mt-5 sm:mt-6 pt-4 sm:pt-5 border-t border-gray-100 flex flex-wrap items-center justify-center gap-3 sm:gap-4 md:gap-6">
                  <div className="flex items-center gap-1.5 text-gray-500 text-xs sm:text-sm">
                    <i className="ri-shield-check-fill text-orange-500"></i>
                    <span>No hidden fees</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-gray-500 text-xs sm:text-sm">
                    <i className="ri-time-fill text-teal-500"></i>
                    <span>Instant quotes</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-gray-500 text-xs sm:text-sm">
                    <i className="ri-map-pin-fill text-rose-500"></i>
                    <span>12 UK cities</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* After-Hours Toggle - Show when valid postcode */}
          {isValidPostcode && locationData && cityPricing && (
            <div className="max-w-2xl mx-auto mt-5 sm:mt-6 md:mt-8">
              <div className="bg-gradient-to-br from-[#22304e] to-[#2a3a5e] rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 lg:p-8 text-white border border-white/10">
                <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg">
                    <i className="ri-time-line text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold">After-Hours Pricing</h3>
                    <p className="text-white/70 text-xs sm:text-sm">Toggle to see adjusted rates</p>
                  </div>
                </div>

                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-center justify-between bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4 border border-white/10 gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold mb-0.5 sm:mb-1 text-sm sm:text-base">After Hours (6pm - 6am)</div>
                      <div className="text-xs sm:text-sm text-white/70">+20% uplift on hourly rates</div>
                    </div>
                    <button
                      onClick={() => setIsAfterHours(!isAfterHours)}
                      className={`relative w-12 sm:w-14 h-7 sm:h-8 rounded-full transition-colors cursor-pointer flex-shrink-0 ${
                        isAfterHours ? 'bg-gradient-to-r from-orange-500 to-amber-500' : 'bg-white/20'
                      }`}
                    >
                      <div
                        className={`absolute top-0.5 sm:top-1 left-0.5 sm:left-1 w-6 h-6 bg-white rounded-full transition-transform shadow-md ${
                          isAfterHours ? 'translate-x-5 sm:translate-x-6' : 'translate-x-0'
                        }`}
                      ></div>
                    </button>
                  </div>

                  {isAfterHours && (
                    <div className="flex items-center justify-between bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4 border border-white/10 gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold mb-0.5 sm:mb-1 text-sm sm:text-base">Short Notice Booking</div>
                        <div className="text-xs sm:text-sm text-white/70">+£50 late callout fee</div>
                      </div>
                      <button
                        onClick={() => setIsShortNotice(!isShortNotice)}
                        className={`relative w-12 sm:w-14 h-7 sm:h-8 rounded-full transition-colors cursor-pointer flex-shrink-0 ${
                          isShortNotice ? 'bg-gradient-to-r from-orange-500 to-amber-500' : 'bg-white/20'
                        }`}
                      >
                        <div
                          className={`absolute top-0.5 sm:top-1 left-0.5 sm:left-1 w-6 h-6 bg-white rounded-full transition-transform shadow-md ${
                            isShortNotice ? 'translate-x-5 sm:translate-x-6' : 'translate-x-0'
                          }`}
                        ></div>
                      </button>
                    </div>
                  )}

                  {(isAfterHours || isShortNotice) && (
                    <div className="bg-orange-500/20 border border-orange-500/30 rounded-lg sm:rounded-xl p-3 sm:p-4">
                      <div className="flex gap-2 sm:gap-3">
                        <i className="ri-information-line text-orange-400 text-lg sm:text-xl flex-shrink-0"></i>
                        <div className="text-xs sm:text-sm text-white/90">
                          {isAfterHours && !isShortNotice && (
                            <p>
                              After-hours pricing applies to bookings between 6pm and 6am with a 20% uplift on hourly rates.
                            </p>
                          )}
                          {isAfterHours && isShortNotice && (
                            <p>
                              After-hours + short notice bookings include a 20% uplift on hourly rates plus a £50 late callout fee.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Pricing Cards Section - Show when valid postcode */}
          {isValidPostcode && locationData && cityPricing && (
            <TaskBudyPricingCardsCity
              city={locationData.city}
              pricing={cityPricing}
              isAfterHours={isAfterHours}
              isShortNotice={isShortNotice}
            />
          )}
        </div>
      </section>

      {/* Why Prices Vary Section */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#22304e] via-[#2a3a5e] to-[#1a2540]"></div>

        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-64 sm:w-80 md:w-96 h-64 sm:h-80 md:h-96 bg-gradient-to-br from-orange-500 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-56 sm:w-64 md:w-80 h-56 sm:h-64 md:h-80 bg-gradient-to-tr from-teal-500 to-transparent rounded-full blur-3xl"></div>
        </div>

        {/* Pattern */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
            backgroundSize: '20px 20px',
          }}
        ></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10 md:mb-14">
            <div className="inline-flex items-center gap-1.5 sm:gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6">
              <i className="ri-question-fill text-orange-400 text-sm sm:text-base"></i>
              <span>Understanding Our Pricing</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4">Why Prices Vary by City</h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-300 max-w-2xl mx-auto px-2">
              Taskbudy connects you with local professionals, so pricing can vary based on several factors
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-8 sm:mb-10 md:mb-12">
            {[
              { icon: 'ri-map-pin-line', title: 'City & Postcode', desc: 'Location-based pricing for your area', color: 'from-orange-500 to-amber-500' },
              { icon: 'ri-team-line', title: 'Demand & Availability', desc: 'Real-time professional availability', color: 'from-teal-500 to-emerald-500' },
              { icon: 'ri-time-line', title: 'Job Duration', desc: 'Time required to complete the task', color: 'from-rose-500 to-pink-500' },
              { icon: 'ri-flashlight-line', title: 'Emergency Response', desc: 'Faster service when you need it most', color: 'from-sky-500 to-cyan-500' },
            ].map((item, index) => (
              <div
                key={index}
                className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 hover:bg-white/10 transition-all duration-300 hover:-translate-y-1"
              >
                <div className={`w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 bg-gradient-to-br ${item.color} rounded-lg sm:rounded-xl flex items-center justify-center mb-2 sm:mb-3 md:mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  <i className={`${item.icon} text-lg sm:text-xl md:text-2xl text-white`}></i>
                </div>
                <h3 className="text-sm sm:text-base md:text-lg font-bold text-white mb-1 sm:mb-2">{item.title}</h3>
                <p className="text-gray-400 text-xs sm:text-sm leading-relaxed hidden sm:block">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Guarantee Card */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8 mb-8 sm:mb-10 md:mb-12">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6 text-center sm:text-left">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                <i className="ri-shield-check-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 sm:mb-3">Transparent Pricing Guarantee</h3>
                <p className="text-gray-300 text-sm sm:text-base md:text-lg leading-relaxed">
                  You&apos;ll always see the final price before confirming your booking — no surprises, no hidden fees. What you see
                  is what you pay.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3 sm:mb-4">Ready to Book?</h3>
            <p className="text-sm sm:text-base md:text-lg text-gray-300 mb-6 sm:mb-8 max-w-xl mx-auto px-2">
              Get a trusted professional at your door — often within the hour.
            </p>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#ff751f] to-[#ff9147] text-white px-6 sm:px-8 md:px-10 py-3 sm:py-4 rounded-xl font-semibold hover:from-[#e56a0f] hover:to-[#ff751f] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer text-sm sm:text-base"
            >
              Book Your Service Now
              <i className="ri-arrow-right-line text-base sm:text-lg"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing FAQ Section */}
      <PricingFAQ />

      <Footer />
    </div>
  );
};

// Pricing Cards Component
interface PricingCardsProps {
  city: string;
  pricing: any;
  isAfterHours: boolean;
  isShortNotice: boolean;
}

const cards = [
  {
    key: 'handyman',
    icon: 'ri-tools-fill',
    title: 'Handyman Services',
    bullets: ['Furniture assembly', 'TV mounting', 'Shelves & wall mounting', 'Door repairs & handles'],
    footerType: 'hourly',
    helper: 'Minimum first hour, then half-hour increments.',
    color: 'from-orange-500 to-amber-500',
  },
  {
    key: 'plumbing',
    icon: 'ri-drop-fill',
    title: 'Plumbing Services',
    bullets: ['Leaking tap repair', 'Blocked sink/drain', 'Toilet repairs', 'Shower repair'],
    footerType: 'hourly',
    helper: 'Minimum first hour, then half-hour increments.',
    color: 'from-sky-500 to-cyan-500',
  },
  {
    key: 'electrical',
    icon: 'ri-flashlight-fill',
    title: 'Electrical Services',
    bullets: ['Replace light fitting', 'Socket replacement', 'EICR safety check', 'Fault finding'],
    footerType: 'hourly',
    helper: 'Minimum first hour, then half-hour increments.',
    color: 'from-amber-500 to-yellow-500',
  },
  {
    key: 'cleaning',
    icon: 'ri-brush-fill',
    title: 'Cleaning Services',
    bullets: ['Regular home cleaning', 'Deep cleaning', 'End of tenancy cleaning', 'One-off clean'],
    footerType: 'hourly',
    helper: 'Minimum first hour, then half-hour increments.',
    color: 'from-teal-500 to-emerald-500',
  },
  {
    key: 'waste',
    icon: 'ri-delete-bin-fill',
    title: 'Waste Removal',
    bullets: ['Household waste', 'Garden waste', 'Furniture disposal', 'DIY rubble / renovations'],
    footerType: 'wasteLoads',
    wasteLines: ['£50 booking fee', 'Quarter load — £90', 'Half load — £180', 'Three-quarter load — £270', 'Full load — £360'],
    helper: 'Describe your waste during booking.',
    color: 'from-rose-500 to-pink-500',
  },
  {
    key: 'transport',
    icon: 'ri-truck-fill',
    title: 'Transportation & Moving',
    bullets: ['Man & van small move', 'Single item delivery', 'Marketplace pickup', 'Storage pickup/drop-off'],
    footerType: 'fixedLines',
    lines: ['£50 booking fee', '£1.50 per mile'],
    color: 'from-indigo-500 to-violet-500',
  },
];

function PricingCard({
  item,
  pricing,
  isAfterHours,
  isShortNotice,
}: {
  item: typeof cards[0];
  pricing: any;
  isAfterHours: boolean;
  isShortNotice: boolean;
}) {
  const getPrice = () => {
    if (!pricing) return null;

    const serviceTypeMap: { [key: string]: string } = {
      handyman: 'handyman',
      plumbing: 'plumbing',
      electrical: 'electrical',
      cleaning: 'cleaning',
    };

    const serviceType = serviceTypeMap[item.key];
    if (serviceType && pricing[serviceType]) {
      return pricing[serviceType];
    }

    return null;
  };

  const priceData = getPrice();

  const calculatePrice = (basePrice: number) => {
    if (!isAfterHours) return basePrice;
    return basePrice * 1.2;
  };

  const firstHourPrice = priceData ? calculatePrice(Number(priceData.first_hour || priceData.first_hour_rate)) : null;
  const halfHourPrice = priceData ? calculatePrice(Number(priceData.half_hour || priceData.half_hour_rate)) : null;

  const isTimeBasedService = ['handyman', 'plumbing', 'electrical', 'cleaning'].includes(item.key);

  const handleBookNow = () => {
    window.location.href = '/';
  };

  return (
    <div className="group relative bg-white rounded-xl sm:rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 border-2 border-gray-100 hover:-translate-y-1 sm:hover:-translate-y-2 flex flex-col h-full">
      {/* Top accent bar */}
      <div className={`h-1.5 w-full bg-gradient-to-r ${item.color}`}></div>

      <div className="p-4 sm:p-5 md:p-6 flex flex-col flex-grow">
        {/* Icon */}
        <div className={`w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 bg-gradient-to-br ${item.color} rounded-lg sm:rounded-xl flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
          <i className={`${item.icon} text-lg sm:text-xl md:text-2xl text-white`}></i>
        </div>

        <h3 className="text-base sm:text-lg md:text-xl font-bold text-[#22304e] mb-2 sm:mb-3 md:mb-4">{item.title}</h3>

        <ul className="space-y-1.5 sm:space-y-2 mb-4 sm:mb-5 md:mb-6 flex-grow">
          {item.bullets.map((b) => (
            <li key={b} className="flex items-start gap-1.5 sm:gap-2">
              <i className="ri-checkbox-circle-fill text-orange-500 mt-0.5 flex-shrink-0 text-sm sm:text-base"></i>
              <span className="text-gray-600 text-xs sm:text-sm">{b}</span>
            </li>
          ))}
        </ul>

        {/* Pricing */}
        <div className="mt-auto">
          {item.footerType === 'hourly' && (
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-3 sm:mb-4 border border-orange-100">
              {firstHourPrice && halfHourPrice ? (
                <>
                  {isAfterHours && isTimeBasedService && (
                    <div className="mb-3 pb-3 border-b border-orange-200">
                      <div className="flex items-center gap-1.5 sm:gap-2 text-orange-600 text-xs sm:text-sm font-bold mb-2">
                        <i className="ri-time-line"></i>
                        <span>After-Hours Pricing</span>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5 text-[10px] sm:text-xs text-gray-600">
                          <i className="ri-moon-line text-orange-500"></i>
                          <span className="font-semibold">6pm - 6am:</span>
                          <span>+20% uplift</span>
                        </div>
                        {isShortNotice && (
                          <div className="flex items-center gap-1.5 text-[10px] sm:text-xs text-gray-600">
                            <i className="ri-alarm-warning-line text-orange-500"></i>
                            <span className="font-semibold">After 8pm:</span>
                            <span>+£50 late callout</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex items-baseline gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                    <span className="text-lg sm:text-xl md:text-2xl font-bold text-orange-600">£{firstHourPrice.toFixed(2)}</span>
                    <span className="text-gray-500 text-xs sm:text-sm font-medium">first hour</span>
                  </div>
                  <div className="flex items-baseline gap-1.5 sm:gap-2">
                    <span className="text-sm sm:text-base md:text-lg font-bold text-orange-600">£{halfHourPrice.toFixed(2)}</span>
                    <span className="text-gray-500 text-xs sm:text-sm font-medium">every half hour after</span>
                  </div>

                  <p className="text-gray-500 text-[10px] sm:text-xs mt-2 sm:mt-3">{item.helper}</p>
                </>
              ) : (
                <>
                  <div className="flex items-baseline gap-1.5 sm:gap-2">
                    <span className="text-sm sm:text-base md:text-lg font-bold text-gray-400">Enter postcode</span>
                    <span className="text-gray-400 text-xs sm:text-sm">for pricing</span>
                  </div>
                  <p className="text-gray-500 text-[10px] sm:text-xs mt-1.5 sm:mt-2">{item.helper}</p>
                </>
              )}
            </div>
          )}

          {item.footerType === 'fixedLines' && (
            <div className="bg-gradient-to-br from-indigo-50 to-violet-50 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-3 sm:mb-4 border border-indigo-100">
              {item.lines?.map((line) => (
                <div key={line} className="text-indigo-600 font-bold mb-0.5 sm:mb-1 text-sm sm:text-base">
                  {line}
                </div>
              ))}
            </div>
          )}

          {item.footerType === 'wasteLoads' && (
            <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-3 sm:mb-4 border border-rose-100">
              {item.wasteLines?.map((line) => (
                <div key={line} className="text-rose-600 font-bold text-xs sm:text-sm mb-0.5 sm:mb-1">
                  {line}
                </div>
              ))}
              <p className="text-gray-500 text-[10px] sm:text-xs mt-1.5 sm:mt-2">{item.helper}</p>
            </div>
          )}

          <button
            className={`w-full py-2.5 sm:py-3 bg-gradient-to-r ${item.color} text-white rounded-lg sm:rounded-xl font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105 cursor-pointer flex items-center justify-center gap-1.5 sm:gap-2 text-sm sm:text-base`}
            onClick={handleBookNow}
          >
            Book now
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </div>
    </div>
  );
}

function TaskBudyPricingCardsCity({ city, pricing, isAfterHours, isShortNotice }: PricingCardsProps) {
  return (
    <section className="py-8 sm:py-10 md:py-12 lg:py-16">
      <div className="text-center mb-6 sm:mb-8 md:mb-10">
        <div className="inline-flex items-center gap-1.5 sm:gap-2 bg-gradient-to-r from-teal-500/10 to-emerald-500/10 border border-teal-200/60 text-teal-600 px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
          <i className="ri-map-pin-fill"></i>
          <span>{city}</span>
        </div>
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#22304e] mb-2 sm:mb-3">Service Pricing for {city}</h2>
        <p className="text-gray-600 max-w-2xl mx-auto text-sm sm:text-base px-2">
          Hourly services have a minimum first hour, then half-hour increments. Waste removal is priced by load size.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
        {cards.map((c) => (
          <PricingCard key={c.key} item={c} pricing={pricing} isAfterHours={isAfterHours} isShortNotice={isShortNotice} />
        ))}
      </div>
    </section>
  );
}

export default PricingPage;
